import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  BRANDING_QUERY_KEY,
  DEFAULT_LOGO_URL,
  DEFAULT_FAVICON_URL,
  DEFAULT_PROFILE_IMAGE_URL,
  DEFAULT_FOOTER_LOGO_URL,
  DEFAULT_OG_IMAGE_URL,
} from "@/constants/branding";

interface BrandingAssets {
  logoUrl: string;
  faviconUrl: string;
  defaultProfileImageUrl: string;
  footerLogoUrl: string;
  ogImageUrl: string;
  isLoading: boolean;
}

export function useBrandingAssets(): BrandingAssets {
  const { data, isLoading } = useQuery({
    queryKey: [BRANDING_QUERY_KEY],
    queryFn: async () => {
      const [
        logoResult,
        faviconResult,
        defaultProfileImageResult,
        footerLogoResult,
        ogImageResult,
      ] = await Promise.all([
        supabase.rpc("get_app_text_setting", { setting_key: "site_logo_url" }),
        supabase.rpc("get_app_text_setting", {
          setting_key: "site_favicon_url",
        }),
        supabase.rpc("get_app_text_setting", {
          setting_key: "default_profile_image_url",
        }),
        supabase.rpc("get_app_text_setting", {
          setting_key: "site_footer_logo_url",
        }),
        supabase.rpc("get_app_text_setting", { setting_key: "og_image_url" }),
      ]);

      if (logoResult.error) throw logoResult.error;
      if (faviconResult.error) throw faviconResult.error;
      if (defaultProfileImageResult.error)
        throw defaultProfileImageResult.error;
      if (footerLogoResult.error) throw footerLogoResult.error;
      if (ogImageResult.error) throw ogImageResult.error;

      const logoUrl = logoResult.data || DEFAULT_LOGO_URL;

      return {
        logoUrl,
        faviconUrl: faviconResult.data || DEFAULT_FAVICON_URL,
        defaultProfileImageUrl:
          defaultProfileImageResult.data || DEFAULT_PROFILE_IMAGE_URL,
        footerLogoUrl: footerLogoResult.data || logoUrl,
        ogImageUrl: ogImageResult.data || DEFAULT_OG_IMAGE_URL,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    logoUrl: data?.logoUrl || DEFAULT_LOGO_URL,
    faviconUrl: data?.faviconUrl || DEFAULT_FAVICON_URL,
    defaultProfileImageUrl:
      data?.defaultProfileImageUrl || DEFAULT_PROFILE_IMAGE_URL,
    footerLogoUrl:
      data?.footerLogoUrl || data?.logoUrl || DEFAULT_FOOTER_LOGO_URL,
    ogImageUrl: data?.ogImageUrl || DEFAULT_OG_IMAGE_URL,
    isLoading,
  };
}
